import { useEffect, useRef, useCallback } from 'react';

import { makeNamespacedLog } from '@sb/log';
import type { CommandResult } from '@sb/routine-runner';
import { TapIcon } from '@sb/ui/icons';
import { GUIDED_MODE_INTERVAL_MS, UserSession } from '@sbrc/utils';

import HeartbeatWorker from './guidedModeHeartbeatWorker?worker';
import { useRoutineRunnerHandle } from './useRoutineRunnerHandle';
import useToast from './useToast';

const log = makeNamespacedLog('useGuidedMode');

interface OnRunAdHocCommandArguments {
  onComplete?: () => void;
  onError?: (error?: string) => void;
  onGuidedModeStart?: () => void;
  onGuidedModeStop?: () => void;
  onRunCommand: () => Promise<void | CommandResult>;
  onSuccess?: () => void;
  /**
   * Suppress the "Hold the button to apply changes" hint that fires when a
   * command is interrupted sooner than `MIN_HOLD_MS`. Callers driven by a tap
   * rather than a hold (the gripper widgets) have no button to hold, so the
   * hint would tell the user to do something the UI doesn't offer.
   */
  suppressHoldHint?: boolean;
}

const MIN_HOLD_MS = 200;

// Heartbeat-worker reconnect tuning (SUP-4078). The worker's WebSocket can drop
// mid-hold (a transient network blip, or an aged feathers-jwt the server
// rejects with a 4401 close). When that happens we re-start the worker with a
// freshly-read token rather than letting the routine runner's 300 ms guided
// mode timeout fire and abort the routine.
const GUIDED_MODE_RECONNECT_BASE_MS = 100;
// Cap the retry delay strictly below the routine runner's 300 ms guided-mode
// timeout. Each retry fires an immediate validateGuidedMode on the main
// connection, so retries must recur faster than 300 ms — otherwise guided mode
// lapses between attempts and aborts the hold mid-reconnect. 100 → 200 → 250 →
// 250 ms keeps every gap under the deadman window.
const GUIDED_MODE_RECONNECT_MAX_DELAY_MS = 250;
// A failing hold performs one initial auth (startHeartbeatWorker) plus this
// many reconnect auths. Keep the TOTAL strictly below the server's auth rate
// limit (5 failed auths / 60 s per IP — see websocket-authenticator.ts) so a
// genuinely-dead token can't consume the whole IP budget and lock out the main
// connection's re-auth: 1 + 3 = 4 < 5. We then give up and tell the user to
// re-log-in.
const GUIDED_MODE_MAX_RECONNECT_ATTEMPTS = 3;

type UseGuidedModeArguments = {
  isVizbot?: boolean;
};

/**
 * Build the WebSocket URL for the routine runner heartbeat worker.
 */
function getRoutineRunnerWsUrl(isVizbot: boolean): string {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = isVizbot ? 'vizbot-ws' : 'routine-runner';

  return `${wsProtocol}//${window.location.host}/${path}`;
}

/**
 * Ad-hoc commands can only be executed in the routine runner as long
 * as it is in Guided Mode. To let the routine runner know that Guided
 * Mode is still required, the remote control continually validates
 * the routine runner while the user holds down a Guided Mode action
 * on the page. Callbacks that handle this logic will create
 * intervals and store them here, and when the user releases the Guided
 * Mode action, the interval can be cancelled.
 */
const useGuidedMode = ({ isVizbot }: UseGuidedModeArguments) => {
  const { setToast } = useToast();

  const routineRunnerHandle = useRoutineRunnerHandle({ isVizbot });

  const workerRef = useRef<Worker | null>(null);

  const guidedModeStoppedAt = useRef<number>(Infinity);

  // True while the user is actively holding a guided-mode action; gates whether
  // a worker disconnect should trigger a reconnect.
  const isGuidedModeActiveRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set once we've given up reconnecting, so the exhaustion toast fires at most
  // once per hold even when a single drop delivers both onerror and onclose.
  const reconnectExhaustedRef = useRef(false);

  // The worker's status handler is installed once (the worker is created
  // lazily), so we route it through a ref we keep current — this avoids a stale
  // closure capturing an old `scheduleReconnect`.
  const handleWorkerStatusRef = useRef<(ev: MessageEvent) => void>(() => {});

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  /**
   * Lazily create the Web Worker. The worker opens its own WebSocket to the
   * routine runner and sends `validateGuidedMode` on a timer. Because the
   * worker runs off the main thread, the heartbeat is immune to React renders
   * blocking `requestAnimationFrame` / `setInterval`.
   */
  const ensureWorker = useCallback(() => {
    if (!workerRef.current) {
      workerRef.current = new HeartbeatWorker();

      workerRef.current.onmessage = (ev: MessageEvent) =>
        handleWorkerStatusRef.current(ev);
    }

    return workerRef.current;
  }, []);

  /**
   * (Re)start the off-thread heartbeat worker, reading the *current*
   * `feathers-jwt` from localStorage each time so a reconnect never replays a
   * stale token (the root cause of SUP-4078).
   */
  const startHeartbeatWorker = useCallback(() => {
    const worker = ensureWorker();
    const token = localStorage.getItem('feathers-jwt');
    const wsUrl = getRoutineRunnerWsUrl(Boolean(isVizbot));

    worker.postMessage({
      type: 'start',
      wsUrl,
      token,
      intervalMs: GUIDED_MODE_INTERVAL_MS,
    });
  }, [ensureWorker, isVizbot]);

  /**
   * Schedule a heartbeat reconnect with exponential backoff. Bounded by
   * GUIDED_MODE_MAX_RECONNECT_ATTEMPTS; on exhaustion we stop and ask the user
   * to re-authenticate rather than spinning against the server's auth limiter.
   */
  const scheduleReconnect = useCallback(() => {
    if (!isGuidedModeActiveRef.current || reconnectTimerRef.current !== null) {
      return;
    }

    if (reconnectAttemptsRef.current >= GUIDED_MODE_MAX_RECONNECT_ATTEMPTS) {
      // Toast at most once per hold: a single drop can deliver both onerror and
      // onclose, and every post-exhaustion disconnect re-enters this branch.
      if (!reconnectExhaustedRef.current) {
        reconnectExhaustedRef.current = true;

        log.error(
          'worker.reconnectExhausted',
          'Guided mode heartbeat could not reconnect',
          { attempts: reconnectAttemptsRef.current },
        );

        setToast({
          kind: 'top',
          message:
            'Guided mode lost its connection. Log out and back in, then hold the button again.',
        });
      }

      return;
    }

    const attempt = reconnectAttemptsRef.current;
    reconnectAttemptsRef.current = attempt + 1;

    const delayMs = Math.min(
      GUIDED_MODE_RECONNECT_BASE_MS * 2 ** attempt,
      GUIDED_MODE_RECONNECT_MAX_DELAY_MS,
    );

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;

      if (!isGuidedModeActiveRef.current) {
        return;
      }

      // Bridge the gap on the main-thread connection, then bring the worker's
      // socket back up with a freshly-read token.
      routineRunnerHandle.validateGuidedMode();
      startHeartbeatWorker();
    }, delayMs);
  }, [routineRunnerHandle, startHeartbeatWorker, setToast]);

  const stopHeartbeatWorker = useCallback(() => {
    isGuidedModeActiveRef.current = false;
    clearReconnectTimer();
    reconnectAttemptsRef.current = 0;
    reconnectExhaustedRef.current = false;

    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'stop' });
    }
  }, [clearReconnectTimer]);

  // Keep the worker's status handler current. The worker posts
  // `{ type: 'status', connected }` when its socket (re)connects and
  // disconnects (with `closeCode`, e.g. 4401 for auth-rejected — SUP-3915).
  useEffect(() => {
    handleWorkerStatusRef.current = (ev: MessageEvent) => {
      if (ev.data?.type !== 'status') {
        return;
      }

      if (ev.data.connected === false) {
        log.warn('worker.disconnected', 'Heartbeat worker disconnected', {
          closeCode: ev.data.closeCode,
        });

        // Self-heal while the user is still holding the action so the routine
        // runner's 300 ms guided mode timeout never fires (SUP-4078).
        if (isGuidedModeActiveRef.current) {
          scheduleReconnect();
        }
      } else if (ev.data.connected === true) {
        // A confirmed, authenticated (re)connection resets the *consecutive*
        // failure budget, so the cap trips only on a sustained failure run
        // (e.g. an expired/rejected token), not on a connection that keeps
        // recovering. A pathologically flapping-but-authenticating socket is
        // still made safe by the routine runner's 300 ms guided-mode timeout.
        reconnectAttemptsRef.current = 0;
        reconnectExhaustedRef.current = false;
        // A live heartbeat makes any pending reconnect moot — cancel it so a
        // stale timer can't tear the healthy connection back down.
        clearReconnectTimer();
      }
    };
  }, [scheduleReconnect, clearReconnectTimer]);

  // Clean up worker + any pending reconnect on unmount
  useEffect(() => {
    return () => {
      isGuidedModeActiveRef.current = false;
      clearReconnectTimer();

      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'stop' });
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [clearReconnectTimer]);

  /**
   * `stopGuidedMode` stops the heartbeat worker and sends a stop command.
   * Optional onGuidedModeStop callback for flexibility in adding logic
   */
  const stopGuidedMode = useCallback(
    async (onGuidedModeStop?: () => void) => {
      stopHeartbeatWorker();
      onGuidedModeStop?.();

      if (guidedModeStoppedAt.current === Infinity) {
        guidedModeStoppedAt.current = Date.now();
      }

      if (routineRunnerHandle.getState()?.kind !== 'RoutineRunning') {
        await routineRunnerHandle.stop('Stopped by user');
      }
    },
    [routineRunnerHandle, stopHeartbeatWorker],
  );

  /**
   * Start sending `validateGuidedMode()` to the routine runner via a Web Worker.
   *
   * The worker opens a dedicated WebSocket connection and sends heartbeat
   * messages on its own timer, completely off the main thread. This ensures the
   * 300 ms backend timeout is never hit, even when heavy React renders block
   * the main thread for extended periods (e.g. large routines with hundreds of
   * step components causing >300 ms render passes).
   *
   * We also fire one immediate validation on the main thread so the backend
   * sees the heartbeat before the worker's WebSocket connection is established.
   *
   * NB `startGuidedMode` is *sync*: we need to avoid any async code before the
   * heartbeat is started, otherwise `stopGuidedMode` could run first.
   */
  const startGuidedMode = useCallback(
    (onGuidedModeStart?: () => void) => {
      isGuidedModeActiveRef.current = true;
      reconnectAttemptsRef.current = 0;
      reconnectExhaustedRef.current = false;
      clearReconnectTimer();

      // Fire one immediate validation on the main thread's existing connection
      // so the backend sees a heartbeat before the worker's socket has
      // authenticated.
      routineRunnerHandle.validateGuidedMode();

      // Start the off-thread heartbeat worker.
      startHeartbeatWorker();

      onGuidedModeStart?.();
    },
    [routineRunnerHandle, startHeartbeatWorker, clearReconnectTimer],
  );

  const runAdHocCommand = useCallback(
    async ({
      onComplete,
      onError,
      onGuidedModeStart,
      onGuidedModeStop,
      onRunCommand,
      onSuccess,
      suppressHoldHint = false,
    }: OnRunAdHocCommandArguments) => {
      startGuidedMode(onGuidedModeStart);
      const guidedModeStatedAt = Date.now();
      guidedModeStoppedAt.current = Infinity;

      const handleError = async (errorMessage: string) => {
        // Don't stop a routine the ad-hoc command never interrupted.
        if (routineRunnerHandle.getState()?.kind !== 'RoutineRunning') {
          await routineRunnerHandle.stop(errorMessage);
        }

        if (onError) {
          onError(errorMessage);
        } else {
          log.error(`runAdHocCommand.error`, 'Error running adhoc command', {
            message: errorMessage,
          });
        }
      };

      try {
        if (!isVizbot) {
          // Show the current user as running an ad-command command.
          UserSession.moveRobot(true);
        }

        const commandResult = await onRunCommand();

        /**
         * Some ad-hoc commands don't throw an error when they fail. Instead, they return
         * a string specifying the outcome of the command.
         */
        if (commandResult === 'NonIdle') {
          // A refusal, not a failure — the command never started. Returned for
          // any non-Idle state, so name the state rather than assuming a run.
          const stateKind = routineRunnerHandle.getState()?.kind;

          log.warn('runAdHocCommand.nonIdle', 'Ad-hoc command refused', {
            stateKind,
          });

          setToast({
            kind: 'error',
            message:
              stateKind === 'RoutineRunning'
                ? "The robot is running a routine and can't be moved."
                : "The robot is busy and can't be moved.",
          });
        } else if (
          commandResult &&
          commandResult !== 'Success' &&
          commandResult !== 'Interrupted'
        ) {
          handleError(`Failure: ${commandResult}`);
        }

        if (commandResult === 'Success' && onSuccess) {
          onSuccess();
        }

        if (
          !suppressHoldHint &&
          (commandResult === 'Interrupted' || commandResult === undefined) &&
          guidedModeStoppedAt.current - guidedModeStatedAt < MIN_HOLD_MS
        ) {
          setToast({
            kind: 'top',
            renderAsComponent: true,
            message: (
              <>
                <TapIcon size="small" className="tw-inline-block tw-mr-8" />
                Hold the button to apply changes
              </>
            ),
          });
        }
      } catch (e) {
        handleError(e instanceof Error ? e.message : String(e));
      } finally {
        stopGuidedMode(onGuidedModeStop);

        // wait up to 2 seconds for state to stop being RunningAdHocCommand
        await routineRunnerHandle.waitForState((state) => {
          return (
            state.kind !== 'RunningAdHocCommand' && state.kind !== 'Recovering'
          );
        }, 2000);

        if (onComplete) onComplete();

        if (!isVizbot) {
          // Makes sure this user is no longer marked as "running ad-hoc command".
          UserSession.moveRobot(false);
        }
      }
    },
    [isVizbot, routineRunnerHandle, startGuidedMode, stopGuidedMode, setToast],
  );

  return {
    startGuidedMode,
    stopGuidedMode,
    runAdHocCommand,
  };
};

export default useGuidedMode;
